/**
 * Device Detail Page
 * Shows device information and dataflow visualization
 */

import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { fetchDevice } from './api';
import DeviceDataFlowVisualizer from './components/DeviceDataFlowVisualizer';
import { isMissingIotDevice, missingIotDeviceCopy } from './deviceLookupHonesty';

export default function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: device, isLoading, error } = useQuery({
    queryKey: ['iot', 'device', id],
    queryFn: () => fetchDevice(id!),
    enabled: !!id,
    refetchInterval: 15000,
    retry: (failureCount, err) => !isMissingIotDevice(err) && failureCount < 2,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !isMissingIotDevice(error)) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load device: {String(error)}</p>
      </div>
    );
  }

  if (!device) {
    const copy = missingIotDeviceCopy(id || '');
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 space-y-3">
        <h1 className="text-lg font-semibold text-amber-950">{copy.title}</h1>
        <p className="text-sm text-amber-900">{copy.body}</p>
        <div className="flex flex-wrap gap-3 text-sm">
          <Link to="/iotshack/devices" className="text-blue-700 hover:text-blue-800">
            Devices list
          </Link>
          <Link to="/iotshack/models" className="text-blue-700 hover:text-blue-800">
            Models
          </Link>
        </div>
      </div>
    );
  }

  const statusColors = {
    online: 'bg-green-100 text-green-800 border-green-200',
    offline: 'bg-red-100 text-red-800 border-red-200',
    unknown: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/iotshack/models"
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">{device.deviceId}</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColors[device.status]}`}>
              {device.status}
            </span>
          </div>
          <p className="text-gray-600 mt-1">{device.type}</p>
        </div>
      </div>

      {/* Device Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Details Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Device Information</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Device ID</dt>
                <dd className="mt-1 text-sm text-gray-900">{device.deviceId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Type</dt>
                <dd className="mt-1 text-sm text-gray-900">{device.type}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900 capitalize">{device.status}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Tenant</dt>
                <dd className="mt-1 text-sm text-gray-900">{device.tenant}</dd>
              </div>
              {device.gatewayId && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Gateway</dt>
                  <dd className="mt-1 text-sm text-gray-900">{device.gatewayId}</dd>
                </div>
              )}
              {device.productId && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Product ID (PIM)</dt>
                  <dd className="mt-1 text-sm text-blue-600 hover:text-blue-800">
                    <Link to={`/pim/products/${device.productId}`}>
                      {device.productId}
                    </Link>
                  </dd>
                </div>
              )}
              {device.sku && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">SKU</dt>
                  <dd className="mt-1 text-sm text-gray-900">{device.sku}</dd>
                </div>
              )}
              {device.templateId && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Template</dt>
                  <dd className="mt-1 text-sm text-gray-900">{device.templateId}</dd>
                </div>
              )}
              {device.decoderId && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Decoder</dt>
                  <dd className="mt-1 text-sm text-blue-600 hover:text-blue-800">
                    <Link to={`/iotshack/decoders`}>
                      {device.decoderId}
                    </Link>
                  </dd>
                </div>
              )}
              {device.lastSeen && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Seen</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(device.lastSeen).toLocaleString()}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(device.createdAt).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(device.updatedAt).toLocaleString()}
                </dd>
              </div>
            </dl>
          </div>

          {/* Metadata Card */}
          {device.metadata && Object.keys(device.metadata).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {device.metadata.manufacturer && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Manufacturer</dt>
                    <dd className="mt-1 text-sm text-gray-900">{device.metadata.manufacturer}</dd>
                  </div>
                )}
                {device.metadata.model && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Model</dt>
                    <dd className="mt-1 text-sm text-gray-900">{device.metadata.model}</dd>
                  </div>
                )}
                {device.metadata.firmware?.version && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Firmware</dt>
                    <dd className="mt-1 text-sm text-gray-900">{device.metadata.firmware.version}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {device.productId && (
                <Link
                  to={`/pim/products/${device.productId}`}
                  className="block w-full px-4 py-2 text-sm text-left text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200"
                >
                  View Product in PIM
                </Link>
              )}
              {device.templateId && (
                <Link
                  to={`/contextualizer/templates/${encodeURIComponent(String(device.templateId))}`}
                  state={{ from: `/iotshack/devices/${device.id}` }}
                  className="block w-full px-4 py-2 text-sm text-left text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200"
                >
                  View Telemetry Template
                </Link>
              )}
              {device.decoderId && (
                <Link
                  to={`/iotshack/decoders`}
                  className="block w-full px-4 py-2 text-sm text-left text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200"
                >
                  View Decoder
                </Link>
              )}
            </div>
          </div>

          {/* Location */}
          {device.location?.coordinates && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Location</h2>
              <p className="text-sm text-gray-600">
                Lat: {device.location.coordinates[1]?.toFixed(6)}<br />
                Lon: {device.location.coordinates[0]?.toFixed(6)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Dataflow Visualization */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Data Flow</h2>
        <p className="text-sm text-gray-600 mb-6">
          Visualizes how data flows from the physical device through the platform
        </p>
        <DeviceDataFlowVisualizer device={device} />
      </div>
    </div>
  );
}
